import React, { useState } from 'react';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { CommentsService } from '~/services/domain/CommentsService';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { useQueryClient } from '@tanstack/react-query';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useTranslation } from 'react-i18next';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Comment } from '~/models/Comment';
import { Post } from '~/models/Post';
import { decodeHTML } from 'entities';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';

export interface IEditCommentProps extends IContextSheetContentProps {
  payload?: {
    comment: Comment;
    parentPost?: Post;
  };
  onCommentEdited?: (comment: Comment) => void;
}

export function EditComment({ onClose, onCommentEdited, payload }: IEditCommentProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { syncPostFromServer } = useQueryCacheUtils();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useDelayedAutoFocus();

  const comment = payload?.comment;
  const parentPost = payload?.parentPost;
  const rawText = comment?.Text.endsWith('(Edited)') 
    ? comment.Text.slice(0, -9) // Remove "(Edited)" text
    : comment?.Text || '';
  
  // Decode HTML entities before showing in the editor
  const originalText = decodeHTML(rawText);
  
  const [text, setText] = useState(originalText);

  const handleSubmit = async () => {
    if (!text.trim() || !comment?.Id) return;

    setIsSubmitting(true);
    try {
      const updatedComment = await CommentsService.editComment(comment.Id, text);

      await queryClient.invalidateQueries({
        queryKey: ['comments'],
      });

      if (parentPost) {
        await syncPostFromServer(parentPost.Id);
      }

      showSuccessToast(t('comments.editSuccess'));
      onCommentEdited?.(updatedComment);
      onClose?.();
    } catch (error) {
      console.warn('Failed to edit comment:', error);
      showErrorToast(t('comments.editError'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack className="flex-1 gap-4">
      <Textarea className="flex-1">
        <TextareaInput
          ref={textareaRef}
          placeholder={t('comments.add_placeholder')}
          value={text}
          onChangeText={setText}
          textAlignVertical="top"
          className="m-0"
        />
      </Textarea>
      <HStack className="shrink-0 justify-end">
        <Button
          onPress={handleSubmit}
          disabled={isSubmitting || !text.trim() || text === originalText}
          className="disabled:bg-primary-200"
          action="primary"
          size="md">
          <ButtonText className={isSubmitting || !text.trim() || text === originalText ? 'text-white/50' : ''}>
            {t('common.update')}
          </ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}

EditComment.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'md',
  adjustForWebKeyboard: true,
} satisfies ISheetConfig;
